import { FC, useState } from 'react';

import { ShopOrder } from '@shared/api/shopOrder/shopOrder.interface';
import { User } from '@shared/api/user/user.interface';
import { PaymentState } from '@shared/types';

interface Props {
  guest: User<string, undefined, undefined, ShopOrder>;
}

export const GuestCard: FC<Props> = function GuestCard({ guest }: Props) {
  const name = guest.info.name;
  const shopOrderList = guest.storeCustomerDetail?.shopOrders;
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  const isPaidGuest = shopOrderList?.some(
    (order) =>
      order.paymentDetail.status === PaymentState.PAID ||
      order.paymentDetail.status === PaymentState.CONFIRMED,
  );

  // Find the last order based on createdAt
  const lastOrder = shopOrderList?.length
    ? [...shopOrderList].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA; // Sort in descending order (newest first)
      })[0]
    : null;

  // Format the last order date if it exists
  const lastOrderDate = lastOrder?.createdAt
    ? new Date(lastOrder.createdAt).toLocaleDateString('ko-KR', {
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '구매 내역 없음';

  const guestCreatedAt = guest.createdAt
    ? new Date(guest.createdAt).toLocaleDateString('ko-KR', {
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '구매 내역 없음';

  return (
    <div className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md">
      {/* 상태 태그 */}
      {isPaidGuest ? (
        <span className="absolute -top-2 right-2 break-keep rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 shadow-sm">
          구매완료
        </span>
      ) : (
        <span className="absolute -top-2 right-2 break-keep rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800 shadow-sm">
          구매유도 필요
        </span>
      )}

      {/* 헤더 섹션 */}
      <div className="mb-3 flex items-center">
        <div className="flex items-center gap-2">
          <h3 className="break-keep font-medium text-gray-800">{name}</h3>
          <p className="break-keep text-sm text-gray-600">{guest.info.phoneNumber}</p>
        </div>
      </div>

      {/* 주문 정보 섹션 */}
      <div className="mb-3 space-y-1.5 border-b border-gray-100 pb-3 text-sm">
        {lastOrder && (
          <>
            {/* 빈소 주소 - 라벨 없이 전체 너비 사용 */}
            <div className="mb-1.5 break-keep text-gray-800">
              {lastOrder.deliveryDetail.receiverAddress || '-'}
            </div>

            <div className="flex">
              <span className="w-20 flex-shrink-0 break-keep text-gray-500">상주님</span>
              <span className="break-keep text-gray-800">
                {lastOrder.deliveryDetail.receiverName || '-'}
              </span>
            </div>
            <div className="flex">
              <span className="w-20 flex-shrink-0 break-keep text-gray-500">
                보내는분
              </span>
              <span className="break-keep text-gray-800">
                {lastOrder.bugoDetail.senderPhrase || '-'}
              </span>
            </div>
            <div className="flex">
              <span className="w-20 flex-shrink-0 break-keep text-gray-500">
                조의문구
              </span>
              <span className="break-keep text-gray-800">
                {lastOrder.bugoDetail.condolencePhrase || '-'}
              </span>
            </div>
          </>
        )}
      </div>

      {/* 푸터 섹션 - 결제 정보 */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <span className="break-keep">최근:</span>
          <span className="break-keep font-medium text-gray-700">{lastOrderDate}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="break-keep">최초:</span>
          <span className="break-keep font-medium text-gray-700">{guestCreatedAt}</span>
        </div>
        <div
          className="flex cursor-pointer items-center justify-center gap-1 hover:text-blue-600"
          onClick={() => setShowOrderDetails(!showOrderDetails)}
        >
          <span className="break-keep">결제시도:</span>
          <span className="break-keep font-medium text-gray-700">
            {shopOrderList?.length || 0}건
          </span>
          <span className="ml-1">{showOrderDetails ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* 결제시도 상세 정보 */}
      {showOrderDetails && shopOrderList && shopOrderList.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-3 text-xs">
          <h4 className="mb-2 font-medium text-gray-700">결제시도 상세 내역</h4>
          <div className="space-y-1">
            {shopOrderList.map((order, index) => {
              const orderDate = order.createdAt
                ? new Date(order.createdAt).toLocaleDateString('ko-KR', {
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })
                : '-';

              return (
                <div key={index} className="rounded bg-gray-50 p-1">
                  <div className="flex justify-between">
                    <span className="text-gray-700">
                      {order.bugoDetail.senderPhrase || '-'}
                    </span>
                    <span className="text-gray-500">{orderDate}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
